import { BillItem } from '../pages/hospital/Billing/types';
import { TYPE_BILL_ITEM, TYPES_DISCOUNT } from '../constants/types';
import { calculateTotalBill } from './bill';

/**
 * Processed Bill Item for Display
 * Contains all computed values and formatting ready for rendering
 */
export interface ProcessedBillItem {
  id: number;
  concept: string;
  quantity: number | string;
  amount: string; // Formatted with locale
  subtotal: string; // Formatted with locale
  color: string; // 'red', 'blue', or 'black'
  isTotal?: boolean;
  isSummary?: boolean;
  isBold?: boolean;
}

/**
 * Process bill items for display in both MaterialUI and PDF formats
 * Extracts the business logic from BillItems.tsx renderInsertedBillItems()
 * 
 * @param items - Array of bill items to process
 * @param currency - Currency code (e.g., 'XOF')
 * @param locale - Locale for number formatting (e.g., 'fr')
 * @param print - Whether this is for print/PDF (filters out certain item types)
 * @returns Array of processed items ready for display
 */
export function processBillItemsForDisplay(
  items: BillItem[],
  currency: string,
  locale: string,
  print: boolean
): ProcessedBillItem[] {
  const processedItems: ProcessedBillItem[] = [];
  
  // Filter items for print mode (lines 504-507 from BillItems.tsx)
  let filteredItems = [...items];
  if (print) {
    filteredItems = items.filter((item) => {
      return ![TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE].includes(item.type);
    });
  }

  // Helper function to get amount (handles amountAlter for distribution)
  // From BillItems.tsx line 272
  const getAmount = (val: BillItem): number => {
    return print && val.amountAlter ? val.amountAlter : Number(val.amount);
  };

  // Process each item (based on lines 509-589 from BillItems.tsx)
  filteredItems.forEach((val, index) => {
    // Skip DISCOUNT_ADDITIONAL_INFO in print mode
    if (val.type === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO && print) {
      return;
    }

    // Determine color based on type (line 514)
    let color = 'black';
    if (TYPES_DISCOUNT.includes(parseInt(val.type.toString()))) {
      color = '#b71c1c'; // red[900]
    } else if (parseInt(val.type.toString()) === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO) {
      color = '#0d47a1'; // blue[900]
    }

    // Calculate amount (lines 519-527)
    const amount = TYPES_DISCOUNT.includes(Number(val.type)) 
      ? getAmount(val) * -1 
      : getAmount(val);
    
    // Replace non-breaking spaces and other special space characters with regular spaces
    let amountString = new Intl.NumberFormat(locale).format(amount).replace(/[\u00A0\u202F\u2009]/g, ' ');
    const percentSymbol = Number(val.type) === TYPE_BILL_ITEM.DISCOUNT_PERCENT ? '%' : '';
    amountString = `${amountString} ${percentSymbol}`;

    // Calculate subtotal (lines 529-541)
    let subtotal = getAmount(val) * Number(val.quantity);
    
    if (Number(val.type) === TYPE_BILL_ITEM.DISCOUNT_PERCENT) {
      // Calculate the percentage of the subtotal
      const removeTypes = print 
        ? [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE]
        : [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO];
      const totalForPercentage = calculateTotalBill(
        items, 
        [TYPE_BILL_ITEM.DISCOUNT_PERCENT, ...removeTypes]
      );
      subtotal = (totalForPercentage * getAmount(val)) / 100;
    }
    
    subtotal = TYPES_DISCOUNT.includes(Number(val.type)) ? subtotal * -1 : subtotal;
    // Replace non-breaking spaces and other special space characters with regular spaces
    const subtotalString = new Intl.NumberFormat(locale).format(subtotal).replace(/[\u00A0\u202F\u2009]/g, ' ');

    processedItems.push({
      id: index,
      concept: val.concept,
      quantity: val.quantity,
      amount: amountString.trim(),
      subtotal: subtotalString,
      color,
    });
  });

  // Add total row if items exist (lines 781-789)
  if (items.length > 0) {
    const removeTypes = print 
      ? [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE] 
      : [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO];
    const totalAmountBill = calculateTotalBill(items, removeTypes);
    
    // Replace non-breaking spaces and other special space characters with regular spaces
    const formattedTotal = new Intl.NumberFormat(locale).format(totalAmountBill).replace(/[\u00A0\u202F\u2009]/g, ' ');
    
    processedItems.push({
      id: items.length,
      concept: 'Total',
      quantity: '',
      amount: '',
      subtotal: `${formattedTotal} ${currency}`,
      color: 'black',
      isTotal: true,
      isBold: true,
    });
  }

  // Add insurance coverage and patient responsibility rows for print mode (lines 793-824)
  if (!print) {
    // Only add these rows in non-print mode, or handle separately in the PDF component
    // This logic is specific to the MaterialUI table view
  } else {
    const hasAdditionalInfo = items.find((item) => {
      return item.type === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO;
    });
    
    if (hasAdditionalInfo) {
      const totalAmountBill = calculateTotalBill(items, [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE]);
      
      // Replace non-breaking spaces and other special space characters with regular spaces
      const formattedInsuranceCoverage = new Intl.NumberFormat(locale).format(Number(hasAdditionalInfo.amount)).replace(/[\u00A0\u202F\u2009]/g, ' ');
      const patientAmount = totalAmountBill - Number(hasAdditionalInfo.amount);
      const formattedPatientAmount = new Intl.NumberFormat(locale).format(patientAmount).replace(/[\u00A0\u202F\u2009]/g, ' ');
      
      processedItems.push({
        id: items.length + 1,
        concept: 'Insurance Coverage',
        quantity: '',
        amount: '',
        subtotal: `${formattedInsuranceCoverage} ${currency}`,
        color: 'black',
        isSummary: true,
      });
      
      processedItems.push({
        id: items.length + 2,
        concept: 'Patient Responsibility',
        quantity: '',
        amount: '',
        subtotal: `${formattedPatientAmount} ${currency}`,
        color: 'black',
        isSummary: true,
      });
      
      processedItems.push({
        id: items.length + 3,
        concept: 'Remaining Amount',
        quantity: '',
        amount: '',
        subtotal: `${formattedPatientAmount} ${currency}`,
        color: 'black',
        isSummary: true,
        isBold: true,
      });
    }
  }

  return processedItems;
}

